import React, { Suspense, useEffect, useRef, useState } from 'react';
import { Image } from 'antd';
import styled from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import ReactGa from 'react-ga4';
import { ImageCounterCenterBottom } from '../../../../components/common/counter/ImageCounterCenterBottom';
import { IProduct, IProductImage } from '../../../../interfaces/Product.interfaces';
import { useCheckUserLoggedIn } from '../../../../hooks/user/useCheckUserLoggedIn';
import { WatermarkCanvas } from './WatermarkCanvas';
import { useNavigate } from 'react-router-dom';
import { ampli } from '../../../../ampli';
import { pageInfo } from '../../../../constants/pageInfo/pageInfo.constant';
import { ProductStatusType } from '../../../../constants/product/product.constant';

interface ProductImageSectionProps {
  images: IProductImage[];
  status: ProductStatusType;
}

export const ProductImageSection = ({ images, status }: ProductImageSectionProps) => {
  const navigate = useNavigate();
  const [activeSlideImageIndex, setActiveSlideImageIndex] = useState(0);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const swiperWrapperRef = useRef<HTMLDivElement>(null);
  const { isUserLoggedIn } = useCheckUserLoggedIn();

  const watermarkIconSizeForLoggedInUser: { width: number; height: number } = { width: 80, height: 12 };

  const handleGuestWatermarkClick = () => {
    ReactGa.event('pvm_click_pdp_product_photo', {
      action: 'click',
      category: 'product_image_view',
      user_type: 'nonmember',
    });
    ampli.pvmClickPdpProductPhoto({ user_type: 'nonmember' });
    navigate(pageInfo.loginPage.path);
  };

  useEffect(() => {
    if (isUserLoggedIn) {
      setCanvasSize(watermarkIconSizeForLoggedInUser);
      return undefined;
    }

    if (!swiperWrapperRef || !swiperWrapperRef.current) {
      return undefined;
    }

    const observer = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      setCanvasSize({ width, height });
    });

    observer.observe(swiperWrapperRef.current);

    return (): void => {
      observer.disconnect();
    };
  }, [isUserLoggedIn]);

  return (
    <>
      {images && images.length > 0 && (
        <SwiperWrapper id={'prod-pictures'} ref={swiperWrapperRef}>
          <Swiper
            slidesPerView={1}
            onSlideChange={(product) => {
              setActiveSlideImageIndex(product.realIndex);
            }}
            edgeSwipeDetection
            loop
          >
            {images
              .sort((a, b) => {
                if (!a.order || !b.order) {
                  return 0;
                }
                return a.order - b.order;
              })
              .filter((slideImage) => slideImage.type !== 'LIST')
              .map((slideImage, slideImageIndex) => {
                return (
                  <CustomSwiperSlide key={slideImage.id}>
                    <WatermarkCanvas
                      width={canvasSize.width}
                      height={canvasSize.height}
                      iconSize={isUserLoggedIn ? watermarkIconSizeForLoggedInUser : undefined}
                      enableOverlay={!isUserLoggedIn}
                      onClick={isUserLoggedIn ? undefined : handleGuestWatermarkClick}
                      text={isUserLoggedIn ? undefined : '매물 사진을 확인하시려면 로그인 해주세요'}
                    />
                    <Image.PreviewGroup
                      preview={{
                        onVisibleChange: (visible: boolean) => {
                          if (visible) {
                            ReactGa.event('pvm_click_pdp_product_photo', {
                              action: 'click',
                              category: 'product_image_view',
                              user_type: 'member',
                            });
                            ampli.pvmClickPdpProductPhoto({ user_type: 'member' });
                          }
                        },
                      }}
                    >
                      {images
                        .sort((a, b) => {
                          if (!a.order || !b.order) {
                            return 0;
                          }
                          return a.order - b.order;
                        })
                        .filter((previewImage) => previewImage.type !== 'LIST')
                        .map((previewImage, previewImageIndex) => {
                          return (
                            <Image
                              src={previewImage.imageUrl}
                              alt={previewImage.imageUrl}
                              style={{
                                width: slideImageIndex === previewImageIndex ? '100%' : '0px',
                                height: slideImageIndex === previewImageIndex ? '100%' : '0px',
                                left: '0',
                                right: '0',
                                objectFit: 'cover',
                              }}
                              preview={{
                                mask: '',
                                onVisibleChange: (value: boolean) => {
                                  if (value === true) {
                                    document.body.style.overflow = 'hidden';
                                  } else {
                                    document.body.style.overflow = 'unset';
                                  }
                                },
                              }}
                            />
                          );
                        })}
                    </Image.PreviewGroup>
                    <div className={'swiper-lazy-preloader'} />
                    {status === 'SOLDOUT' && <Dimmed />}
                  </CustomSwiperSlide>
                );
              })}
          </Swiper>

          {images && images.filter((image) => image.type !== 'LIST').length > 0 && (
            <ImageCounterCenterBottom
              currentPageNum={activeSlideImageIndex + 1}
              totalPageNum={images.filter((image) => image.type !== 'LIST').length}
            />
          )}
          {status === 'SOLDOUT' && (
            <>
              <DimmedCenterMessage1>{'SOLD'}</DimmedCenterMessage1>
              <DimmedCenterMessage2>{'OUT'}</DimmedCenterMessage2>
            </>
          )}
        </SwiperWrapper>
      )}
    </>
  );
};

const SwiperWrapper = styled.div`
  position: relative;
`;

const CustomSwiperSlide = styled(SwiperSlide)`
  width: 100%;
  position: relative;

  cursor: pointer;
`;

const NoAutoHeightImg = styled.img`
  width: 100vw;
  height: 100%;

  position: absolute;

  left: 0;
  top: 0;

  object-fit: cover;
`;

const Dimmed = styled.div`
  position: absolute;

  width: 100%;
  height: 100%;

  top: 0;
  left: 0;

  border-radius: var(--radius-medium, 8px);
  background: rgba(35, 36, 41, 0.6);
  backdrop-filter: blur(4px);
`;

const DimmedCenterMessage1 = styled.span`
  position: absolute;
  left: 0px;
  top: calc(42% - 15px);

  font-weight: 700;
  font-size: 25px;

  width: 100%;
  text-align: center;

  z-index: 50;

  color: #ffffff;
`;

const DimmedCenterMessage2 = styled.span`
  position: absolute;
  left: 0px;
  top: calc(42% + 15px);

  font-weight: 700;
  font-size: 25px;

  width: 100%;
  text-align: center;

  z-index: 50;

  color: #ffffff;
`;
